import { AxiosResponse } from 'axios';
import React, {
  ReactNode,
  useState,
  createContext,
  useContext,
  useMemo,
  useEffect,
  Dispatch
} from 'react';
import { ApiOrderControllerUrl } from '../api/axios.url';
import { CalculateCreditPaymentInterfaceAssignedToConsumer } from '../shared/type/calculatePaymentResponse.type';
import {
  OrderForParentInterface,
  OrderForChildren,
  OrderedItemInterface
} from '../shared/type/orderForParent.type';
import { PlaceOrderForParentInterface } from '../shared/type/placeOrderForParent.type';
import { useApi } from './ApiProvider';
import { BasketAlertInterface } from '../shared/type/basketAlert.type';

interface Props {
  children: ReactNode;
}

interface UserDataInterface {
  consumerId: number;
  saldo: number;
  creditLimit: number;
  provision: string;
}

type OrderType = 'cash' | 'credit';

interface TenantAdminBasketContextProps {
  parentBasketContext: OrderForParentInterface;
  isEmpty: boolean;
  addItemToBasket: (order: OrderForChildren) => void;
  deleteProductFromBasket: (consumerId: number, itemToRemove: OrderedItemInterface) => void;
  changeProductQuantity: (
    consumerId: number,
    itemToChange: OrderedItemInterface,
    increment: boolean
  ) => void;
  resetBasket: () => void;
  setUserData: Dispatch<React.SetStateAction<UserDataInterface[]>>;
  setOrderType: Dispatch<React.SetStateAction<OrderType>>;
  calculatedPayment: CalculateCreditPaymentInterfaceAssignedToConsumer | undefined;
  placeOrderForPayment: () => Promise<void>;
  orderSummary: PlaceOrderForParentInterface | undefined;
  clearBasketForConsumer: (consumerId: number) => void;
  basketAlerts: BasketAlertInterface[];
}

const defaultBasket: OrderForParentInterface = {
  ordersForChildren: []
};

const TenantAdminDashboardContextContext = createContext<TenantAdminBasketContextProps | undefined>(
  undefined
);

export const useTenantAdminBasketContext = () => {
  const context = useContext(TenantAdminDashboardContextContext);
  if (!context) {
    throw new Error(
      'useTenantAdminBasketContext must be used within a TenantAdminBasketContextProvider'
    );
  }
  return context;
};

export const TenantAdminBasketContextProvider = ({ children }: Props) => {
  const { apiOrderController } = useApi();
  const [orderType, setOrderType] = useState<OrderType>('cash');
  const [parentBasketContext, setParentBasketContext] =
    useState<OrderForParentInterface>(defaultBasket);
  const [calculatedPayment, setCalculatedPayment] = useState<
    CalculateCreditPaymentInterfaceAssignedToConsumer | undefined
  >(undefined);
  const [userData, setUserData] = useState<UserDataInterface[]>([]);
  const [orderSummary, setOrderSummary] = useState<PlaceOrderForParentInterface>();
  const [basketAlerts, setBasketAlerts] = useState<BasketAlertInterface[]>([]);

  const [isEmpty, setIsEmpty] = useState<boolean>(true);

  function clearBasketForConsumer(consumerId: number) {
    setParentBasketContext((prevContext) => ({
      ...prevContext,
      ordersForChildren: prevContext.ordersForChildren.filter(
        (order) => order.consumerId !== consumerId
      )
    }));
  }

  const removeAlert = (consumerId: string, stakeId: number, when: string) => {
    setBasketAlerts((prevAlerts) =>
      prevAlerts.map((alert) =>
        alert.consumerId === consumerId && alert.stakeId === stakeId && alert.when === when
          ? { ...alert, visible: false }
          : alert
      )
    );

    setTimeout(() => {
      setBasketAlerts((prevAlerts) =>
        prevAlerts.filter(
          (alert) =>
            alert.consumerId !== consumerId || alert.stakeId !== stakeId || alert.when !== when
        )
      );
    }, 500);
  };

  const addAlert = (text: string, consumerId: string, stakeId: number, when: string) => {
    setBasketAlerts((prevAlerts) => {
      if (
        prevAlerts.some(
          (alert) =>
            alert.consumerId === consumerId &&
            alert.stakeId === stakeId &&
            alert.text === text &&
            alert.when === when
        )
      ) {
        return prevAlerts;
      }
      const newAlert = { text, consumerId, stakeId, when, timeout: 10000, visible: true };
      return [...prevAlerts, newAlert];
    });

    setTimeout(() => {
      removeAlert(consumerId, stakeId, when);
    }, 10000);
  };

  function checkForAlerts(
    consumerId: number,
    isIncrement: boolean,
    item: OrderedItemInterface,
    alreadyBoughtCount: number,
    alreadyInBasked: number
  ) {
    const { itemPurchaseLimit, stakeLimitLeft, stakeId } = item.purchasableItem;

    if (!isIncrement) return false;

    if (stakeLimitLeft !== null && stakeLimitLeft <= 0) {
      addAlert(
        'Przekroczono grupowy limit zakupowy. Pozycja nie może zostać dodana do koszyka.',
        consumerId.toString(),
        stakeId,
        item.when
      );
      return true;
    }

    // 🔴 Poprawka: sprawdzamy liczbę zamówień tylko dla danego dnia!
    const userOrdersForThisDay =
      parentBasketContext.ordersForChildren
        .find((order) => order.consumerId === consumerId)
        ?.orderedItems.filter((orderItem) => orderItem.when === item.when) ?? [];

    const totalUserCountForThisDay = userOrdersForThisDay.reduce(
      (sum, orderItem) => sum + orderItem.count,
      0
    );

    if (itemPurchaseLimit !== null && totalUserCountForThisDay >= itemPurchaseLimit) {
      addAlert(
        'Osobisty limit zakupowy osiągnięty na dany dzień, nie można dodać więcej tego produktu.',
        consumerId.toString(),
        stakeId,
        item.when
      );
      return true;
    }

    return false;
  }

  function addItemToBasket(newItem: OrderForChildren) {
    setParentBasketContext((prevContext) => {
      const existingOrderIndex = prevContext.ordersForChildren.findIndex(
        (order) => order.consumerId === newItem.consumerId
      );

      if (existingOrderIndex !== -1) {
        const existingOrder = prevContext.ordersForChildren[existingOrderIndex];
        let mergedOrderedItems = [...existingOrder.orderedItems];

        newItem.orderedItems.forEach((newOrderedItem) => {
          const existingItemIndex = mergedOrderedItems.findIndex(
            (item) =>
              item.purchasableItem.name === newOrderedItem.purchasableItem.name &&
              item.purchasableItem.price === newOrderedItem.purchasableItem.price &&
              item.when === newOrderedItem.when
          );

          if (existingItemIndex !== -1) {
            const existingItem = mergedOrderedItems[existingItemIndex];
            if (
              checkForAlerts(
                newItem.consumerId,
                true,
                newOrderedItem,
                newOrderedItem.purchasableItem.alreadyBoughtCount,
                existingItem.count
              )
            ) {
              return;
            }
            changeProductQuantity(newItem.consumerId, existingItem, true);
          } else {
            if (checkForAlerts(newItem.consumerId, true, newOrderedItem, newOrderedItem.count, 0)) {
              return;
            }
            mergedOrderedItems.push(newOrderedItem);
          }
        });

        const updatedOrdersForChildren = [...prevContext.ordersForChildren];
        updatedOrdersForChildren[existingOrderIndex] = {
          ...existingOrder,
          orderedItems: mergedOrderedItems
        };

        return {
          ...prevContext,
          ordersForChildren: updatedOrdersForChildren
        };
      } else {
        if (
          newItem.orderedItems.some((item) =>
            checkForAlerts(
              newItem.consumerId,
              true,
              item,
              item.purchasableItem.alreadyBoughtCount,
              0
            )
          )
        ) {
          return prevContext;
        }

        return {
          ...prevContext,
          ordersForChildren: [...prevContext.ordersForChildren, newItem]
        };
      }
    });
  }

  const getOrderTypeUrl = (): ApiOrderControllerUrl => {
    if (orderType === 'cash') {
      return 'calculate-cash-payment';
    } else {
      return 'calculate-credit-payment';
    }
  };

  async function calculatePayment() {
    try {
      const consumerCalculationRequests = parentBasketContext.ordersForChildren
        .map((children) => {
          const foundConsumer = userData.find(
            (consumer) => consumer.consumerId === children.consumerId
          );

          if (foundConsumer) {
            const { saldo, creditLimit, provision } = foundConsumer;

            return {
              consumerId: children.consumerId,
              orderedItems: children.orderedItems,
              initialSaldo: saldo ?? 0,
              creditLimit,
              provision
            };
          } else {
            console.error(`No consumer found with id ${children.consumerId}`);
            return null;
          }
        })
        .filter((request) => request !== null);

      if (consumerCalculationRequests.length > 0) {
        const response: AxiosResponse<CalculateCreditPaymentInterfaceAssignedToConsumer> =
          await apiOrderController(getOrderTypeUrl()).post('', {
            consumerRequests: consumerCalculationRequests
          });

        const updatedPayments: CalculateCreditPaymentInterfaceAssignedToConsumer = response.data;

        setCalculatedPayment(updatedPayments);
      } else {
        console.error('No valid consumer data to process');
      }
    } catch (error) {
      console.error('Error calculating payment:', error);
    }
  }

  const placeOrderForPayment = async () => {
    try {
      // @ts-ignore
      const ordersForChildren = parentBasketContext.ordersForChildren.map((order) => ({
        tenantId: order.tenantId,
        consumerId: order.consumerId,
        useCredit: true,
        orderedItems: order.orderedItems.map((item) => ({
          purchasableItem: {
            name: item.purchasableItem.name,
            meals: item.purchasableItem.meals.map((meal) => ({
              mealToDayId: meal.mealToDayId,
              courseName: meal.courseName,
              mealDto: {
                // @ts-ignore
                id: meal.mealDto.id,
                // @ts-ignore
                name: meal.mealDto.name,
                // @ts-ignore
                description: meal.mealDto.description,
                // @ts-ignore
                allergens: meal.mealDto.allergens.map((allergen) => ({
                  id: allergen.id,
                  formalNumber: allergen.formalNumber,
                  name: allergen.name
                }))
              }
            })),
            price: item.purchasableItem.price,
            originalPrice: item.purchasableItem.originalPrice,
            stakeId: item.purchasableItem.stakeId || 0,
            alreadyBoughtCount: item.purchasableItem.alreadyBoughtCount || 0,
            orderCount: item.purchasableItem.orderCount || 0,
            stakeLimitLeft: item.purchasableItem.stakeLimitLeft ?? null,
            itemPurchaseLimit: item.purchasableItem.itemPurchaseLimit ?? null
          },
          count: item.count,
          when: item.when
        }))
      }));

      const response: AxiosResponse<PlaceOrderForParentInterface> = await apiOrderController(
        'place-order-for-parent'
      ).post('', {
        ordersForChildren: ordersForChildren
      });

      setOrderSummary(response.data);
    } catch (error) {
      console.error('Error placing order:', error);
    }
  };

  function deleteProductFromBasket(consumerId: number, itemToRemove: OrderedItemInterface) {
    setParentBasketContext((prevContext) => ({
      ...prevContext,
      ordersForChildren: prevContext.ordersForChildren.map((order) => {
        if (order.consumerId !== consumerId) return order;

        return {
          ...order,
          orderedItems: order.orderedItems.filter((item) => {
            const isSameProduct =
              item.purchasableItem.name === itemToRemove.purchasableItem.name &&
              item.purchasableItem.price === itemToRemove.purchasableItem.price &&
              (item.when === itemToRemove.when ||
                (item.connectedDates && item.connectedDates.includes(itemToRemove.when)));
            return !isSameProduct;
          })
        };
      })
    }));
  }

  function changeProductQuantity(
    consumerId: number,
    itemToChange: OrderedItemInterface,
    increment: boolean
  ) {
    if (
      checkForAlerts(
        consumerId,
        increment,
        itemToChange,
        itemToChange.purchasableItem.alreadyBoughtCount,
        itemToChange.count
      )
    ) {
      return;
    }
    setParentBasketContext((prevContext) => ({
      ...prevContext,
      ordersForChildren: prevContext.ordersForChildren.map((order) => {
        if (order.consumerId !== consumerId) return order;

        return {
          ...order,
          orderedItems: order.orderedItems.map((item) => {
            const isTargetItem =
              item.purchasableItem.name === itemToChange.purchasableItem.name &&
              item.purchasableItem.price === itemToChange.purchasableItem.price &&
              (item.when === itemToChange.when ||
                (item.connectedDates && item.connectedDates.includes(itemToChange.when)));

            if (isTargetItem) {
              const newCount = increment ? item.count + 1 : Math.max(item.count - 1, 1);
              return { ...item, count: newCount };
            }
            return item;
          })
        };
      })
    }));
  }

  function resetBasket() {
    setParentBasketContext(defaultBasket);
  }

  useEffect(() => {
    if (parentBasketContext.ordersForChildren.length > 0) {
      setIsEmpty(false);
    } else {
      setIsEmpty(true);
    }
    calculatePayment();
  }, [parentBasketContext]);

  const contextValue = useMemo(
    () => ({
      parentBasketContext,
      isEmpty,
      addItemToBasket,
      resetBasket,
      deleteProductFromBasket,
      changeProductQuantity,
      setUserData,
      calculatedPayment,
      placeOrderForPayment,
      orderSummary,
      clearBasketForConsumer,
      setOrderType,
      basketAlerts
    }),
    [
      parentBasketContext,
      isEmpty,
      addItemToBasket,
      resetBasket,
      deleteProductFromBasket,
      changeProductQuantity,
      setUserData,
      calculatedPayment,
      placeOrderForPayment,
      orderSummary,
      clearBasketForConsumer,
      setOrderType,
      basketAlerts
    ]
  );

  return (
    <TenantAdminDashboardContextContext.Provider value={contextValue}>
      {children}
    </TenantAdminDashboardContextContext.Provider>
  );
};
